/**
 * dashboardStatsMapper (Frontend) — pure functions die DashboardStatsResponse
 * in Recharts-Data-Arrays transformieren.
 *
 * Hardcoded Color-Map + Labels — keine Theme-System Abhaengigkeit.
 * Pure: keine React/DOM-Imports.
 *
 * Phase 10 Plan 03. Phase 11: supplierDistribution + mapStatsToSupplierData.
 */

import type { SectionId } from './sectionQueryBuilder';

export interface DashboardStatsResponse {
  trackingStatus: {
    stuck: number;
    open: number;
    awaiting_package: number;
    carrier: number;
    manual_only: number;
    done: number;
    closed_manually: number;
    neverShipped: number;   // Phase 18: Label gedruckt, kein Transport-Scan
    ruecksendung: number;   // 29.07.2026: Paket laeuft zu uns zurueck
  };
  internalStatusInCarrier: {
    pre_transit: number;
    in_transit: number;
    out_for_delivery: number;
    exception: number;
    returned: number;
    unknown: number;
  };
  supplierDistribution: {           // Phase 11: Lieferanten-Verteilung
    huck: number;
    helo: number;
    glawel: number;
    sporttore: number;
    wff: number;                    // 2026-07-27: WFF offen
  };
  supplierCompleted?: {             // Phase 16: Abgeschlossen pro Lieferant (optional fuer Backwards-Compat)
    huck: number;
    helo: number;
    glawel: number;
    sporttore: number;
    wff: number;                    // 2026-07-27: WFF abgeschlossen
  };
  supplierStuck?: {                 // Phase 17: Stuck-Notifications pro Lieferant (optional)
    huck: number;
    helo: number;
    glawel: number;
    sporttore: number;
    wff: number;
  };
}

export type InternalStatusKey = keyof DashboardStatsResponse['internalStatusInCarrier'];

// Phase 11: Lieferanten-Typen
export type SupplierSectionId = 'huck' | 'helo' | 'glawel' | 'sporttore';

export interface DonutEntry {
  sectionId: SectionId;
  label: string;
  value: number;
  color: string;
}

export interface BarEntry {
  dhlStatus: InternalStatusKey;
  label: string;
  value: number;
  color: string;
}

/** Phase 11: Lieferanten-Donut-Segment */
export interface SupplierDonutEntry {
  sectionId: SupplierSectionId;
  label: string;
  value: number;
  color: string;
}

// CI-konforme Farben — inline, RESEARCH.md Section 5
export const SECTION_COLORS: Record<string, string> = {
  stuck:      '#F97316', // orange-500 (konsistent mit stuck-Border in OrderCard)
  carrier:    '#E9FF57', // CI-Gelb (konsistent mit Carrier-Badge yellow-50)
  neverShipped: '#FB923C', // orange-400 (Phase 18: Label gedruckt, nie versendet)
  ruecksendung: '#DC2626',  // red-600 — Ware ist zurueck bei uns, Handlungsbedarf
  waiting:    '#93C5FD', // blue-300
  manualOnly: '#C4B5FD', // purple-300
  done:       '#86EFAC', // green-300
  // Phase 11.1: Lieferanten-Farben (Pastell, konsistent mit Donut-Segmenten)
  huck:       '#C084FC', // purple-400
  helo:       '#F472B6', // pink-400
  glawel:     '#67E8F9', // cyan-300
  sporttore:  '#FCD34D', // amber-300
  // Phase 15: WFF (gruener Ton — interner Lieferant)
  wff:        '#34D399', // emerald-400
  // Phase 14: Nachverfolgungs-Sektion
  followup:   '#FBBF24', // amber-400
  // Phase 16: Abgeschlossen-Sektionen (gedimmte Lieferanten-Farben)
  huckDone:      '#E9D5FF', // purple-200
  heloDone:      '#FBCFE8', // pink-200
  glawelDone:    '#A5F3FC', // cyan-200
  sporttoreDone: '#FDE68A', // amber-200
  wffDone:       '#A7F3D0', // emerald-200 (2026-07-27)
};

export const SECTION_LABELS: Record<string, string> = {
  stuck:      'Stuck/Alarm',
  carrier:    'Beim Carrier',
  neverShipped: 'Nie versendet',
  ruecksendung: 'Paket zurück zu anstoss',
  waiting:    'Wartet auf Paket',
  manualOnly: 'Nur manuell',
  done:       'Abgeschlossen',
  // Phase 11.1: Lieferanten-Labels (vorher nur in SUPPLIER_LABELS, SectionHeader
  // las aber SECTION_LABELS → Lieferanten-Sektionen hatten leeren Titel)
  huck:       'Huck',
  helo:       'Helo',
  glawel:     'GlaWel',
  sporttore:  'Sporttore',
  // Phase 15: WFF — interner Lieferant, ueber items.manufacturer identifiziert
  wff:        'WFF (wartet auf DHL-Abholung)',
  // Phase 16: Abgeschlossen pro Lieferant
  huckDone:      'Huck — abgeschlossen',
  heloDone:      'Helo — abgeschlossen',
  glawelDone:    'GlaWel — abgeschlossen',
  sporttoreDone: 'Sporttore — abgeschlossen',
  wffDone:       'WFF — abgeschlossen',   // 2026-07-27
  // Phase 14: Nachverfolgungs-Tab — eine Sektion
  followup:   'Zur Nachverfolgung markiert',
};

export const INTERNAL_STATUS_COLORS: Record<InternalStatusKey, string> = {
  pre_transit:      '#9CA3AF', // gray-400
  in_transit:       '#3B82F6', // blue-500
  out_for_delivery: '#F97316', // orange-500
  exception:        '#EF4444', // red-500
  returned:         '#A855F7', // purple-500
  unknown:          '#D1D5DB', // gray-300
};

export const INTERNAL_STATUS_LABELS: Record<InternalStatusKey, string> = {
  pre_transit:      'Label gedruckt',
  in_transit:       'Unterwegs',
  out_for_delivery: 'In Zustellung',
  exception:        'Problem',
  returned:         'Retoure',
  unknown:          'Unbekannt',
};

// Phase 11: CI-konforme Pastell-Farben fuer Lieferanten-Donut
export const SUPPLIER_COLORS: Record<SupplierSectionId, string> = {
  huck:      '#C084FC', // purple-400
  helo:      '#F472B6', // pink-400
  glawel:    '#67E8F9', // cyan-300
  sporttore: '#FCD34D', // amber-300
};

export const SUPPLIER_LABELS: Record<SupplierSectionId, string> = {
  huck:      'Huck',
  helo:      'Helo',
  glawel:    'GlaWel',
  sporttore: 'Sporttore',
};

// Reihenfolge: orderStatusId ASC (Huck=5.5 zuerst, Sporttore=5.66 zuletzt)
const SUPPLIER_ORDER: SupplierSectionId[] = ['huck', 'helo', 'glawel', 'sporttore'];

/**
 * Mappt tracking_status-Verteilung auf Donut-Segmente.
 *
 * - stuck: NUR wenn > 0 (sonst irrefuehrendes leeres Segment)
 * - carrier: IMMER (auch wenn 0 — operative Sicht wichtig)
 * - waiting: = open + awaiting_package (Sektion mappt auf 2 Status)
 * - manualOnly: NUR wenn > 0
 * - done: = done + closed_manually (Sektion mappt auf 2 Status), IMMER
 *
 * Reihenfolge: stuck (wenn), carrier, waiting, manualOnly (wenn), done
 */
export function mapStatsToDonutData(stats: DashboardStatsResponse): DonutEntry[] {
  const ts = stats.trackingStatus;
  const out: DonutEntry[] = [];

  if (ts.stuck > 0) {
    out.push({
      sectionId: 'stuck',
      label: SECTION_LABELS.stuck,
      value: ts.stuck,
      color: SECTION_COLORS.stuck,
    });
  }

  // carrier IMMER (auch wenn 0 — operative Bedeutung)
  out.push({
    sectionId: 'carrier',
    label: SECTION_LABELS.carrier,
    value: ts.carrier,
    color: SECTION_COLORS.carrier,
  });

  // waiting = open + awaiting_package (beide Sektion "Wartet auf Paket")
  out.push({
    sectionId: 'waiting',
    label: SECTION_LABELS.waiting,
    value: ts.open + ts.awaiting_package,
    color: SECTION_COLORS.waiting,
  });

  if (ts.manual_only > 0) {
    out.push({
      sectionId: 'manualOnly',
      label: SECTION_LABELS.manualOnly,
      value: ts.manual_only,
      color: SECTION_COLORS.manualOnly,
    });
  }

  // done = done + closed_manually, IMMER
  out.push({
    sectionId: 'done',
    label: SECTION_LABELS.done,
    value: ts.done + ts.closed_manually,
    color: SECTION_COLORS.done,
  });

  return out;
}

/**
 * Mappt internal_status-Verteilung auf BarChart-Bars.
 *
 * - Alle 6 Status werden inkludiert (auch wenn 0 — visuelle Konsistenz)
 * - Sortiert nach value DESC
 * - dhlStatus = string-key (pre_transit, in_transit, ...)
 */
export function mapStatsToBarData(stats: DashboardStatsResponse): BarEntry[] {
  const keys: InternalStatusKey[] = [
    'pre_transit', 'in_transit', 'out_for_delivery', 'exception', 'returned', 'unknown',
  ];

  const entries: BarEntry[] = keys.map(k => ({
    dhlStatus: k,
    label: INTERNAL_STATUS_LABELS[k],
    value: stats.internalStatusInCarrier[k],
    color: INTERNAL_STATUS_COLORS[k],
  }));

  entries.sort((a, b) => b.value - a.value);
  return entries;
}

/**
 * Phase 11: Mappt supplierDistribution auf Donut-Segmente fuer Lieferanten-Tab.
 *
 * - Alle 4 Lieferanten werden inkludiert (auch wenn 0 — operative Sicht)
 * - Sortierung: orderStatusId ASC (Huck → Helo → GlaWel → Sporttore)
 */
export function mapStatsToSupplierData(stats: DashboardStatsResponse): SupplierDonutEntry[] {
  const dist = stats.supplierDistribution;
  return SUPPLIER_ORDER.map(key => ({
    sectionId: key,
    label: SUPPLIER_LABELS[key],
    value: dist[key],
    color: SUPPLIER_COLORS[key],
  }));
}
